import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBell,
  FaBirthdayCake,
  FaCalendarAlt,
  FaCalendarPlus,
  FaTrash,
  FaEdit,
  FaSyncAlt,
  FaPlug,
  FaToggleOn,
  FaToggleOff,
  FaLink,
  FaCalendarCheck,
  FaPlay,
  FaUser,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaUserCheck,
} from "react-icons/fa";
import api from "../api";
import './AgendaPolish.css';
import { useAuth } from "../AuthContext";
import Modal from "../components/Modal";
import ScheduleConfigurator from "../components/ScheduleConfigurator";
import AgendaInlineCalendar from "../components/AgendaInlineCalendar";
import {
  buildScheduleRequest,
  createEmptyScheduleState,
  createStateFromSchedule,
} from "../utils/schedules";
import {
  CALENDAR_SHARE_PERMISSION,
  hasPermission,
} from "../utils/permissions";
const DEFAULT_CALENDAR_PREFERENCES = Object.freeze({
  showEventsOnDashboard: true,
  showBirthdaysOnDashboard: false,
  showCommemorativeDates: false,
});

const WEEKDAY_SHORT = [
  "Dom",
  "Seg",
  "Ter",
  "Qua",
  "Qui",
  "Sex",
  "Sáb",
];

const createDefaultCalendarState = () => ({
  events: [],
  birthdays: [],
  commemorativeDates: [],
  integrations: [],
  preferences: { ...DEFAULT_CALENDAR_PREFERENCES },
  todayMessages: [],
  birthdayGreeting: null,
  schedules: [],
});

const createEmptyEventForm = () => ({
  localId: null,
  title: "",
  description: "",
  date: "",
  time: "",
  notifyOnDashboard: true,
  targetUserIds: [],
  targetSectorIds: [],
  sendToAll: false,
  inviteUserIds: [],
  forceInvite: false,
});

const sanitizeEventPrefill = (prefill) => {
  if (!prefill || typeof prefill !== "object") {
    return {};
  }
  const sanitized = {};
  if (typeof prefill.title === "string") {
    sanitized.title = prefill.title.trim().slice(0, 150);
  }
  if (typeof prefill.description === "string") {
    sanitized.description = prefill.description.trim();
  }
  if (typeof prefill.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(prefill.date.trim())) {
    sanitized.date = prefill.date.trim();
  }
  if (typeof prefill.time === "string" && /^\d{2}:\d{2}$/.test(prefill.time.trim())) {
    sanitized.time = prefill.time.trim();
  }
  return sanitized;
};

const describePrefillHighlights = (prefill = {}) => {
  const highlights = [];
  if (prefill.title) highlights.push(`título "${prefill.title}"`);
  if (prefill.date) highlights.push(`data ${prefill.date}`);
  if (prefill.time) highlights.push(`horário ${prefill.time}`);
  return highlights;
};

export default function Agenda({ refreshKey, assistantAction = null }) {
  const { user } = useAuth();
  const isMasterUser = Boolean(user?.isMaster);
  const [calendarSummary, setCalendarSummary] = useState(() =>
    createDefaultCalendarState()
  );
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState("");
  const [calendarInfo, setCalendarInfo] = useState("");
  const [agendaTab, setAgendaTab] = useState("reminders");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [scheduleTimeFilter, setScheduleTimeFilter] = useState("upcoming");
  const [scheduleDateFilter, setScheduleDateFilter] = useState({ start: "", end: "" });
  const [completedSchedules, setCompletedSchedules] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedError, setCompletedError] = useState("");
  const [completedRequested, setCompletedRequested] = useState(false);
  const [eventForm, setEventForm] = useState(() => createEmptyEventForm());
  const [savingEvent, setSavingEvent] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState(() => ({
    users: [],
    sectors: [],
  }));
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientError, setRecipientError] = useState("");
  const [invitations, setInvitations] = useState([]);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const [integrationForm, setIntegrationForm] = useState({
    provider: "google",
    sourceUrl: "",
    displayName: "",
  });
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [syncingIntegrationIds, setSyncingIntegrationIds] = useState([]);
  const [updatingIntegrationIds, setUpdatingIntegrationIds] = useState([]);
  const [removingIntegrationIds, setRemovingIntegrationIds] = useState([]);
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const eventTitleInputRef = useRef(null);
  const eventFormRef = useRef(null);
  const focusEventForm = useCallback(() => {
    requestAnimationFrame(() => {
      eventFormRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      eventTitleInputRef.current?.focus({ preventScroll: true });
    });
  }, []);
  const handleSelectCalendarDay = useCallback((date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    const dateValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    setCalendarModalOpen(false);
    setEventForm({ ...createEmptyEventForm(), date: dateValue });
    setCalendarError("");
    setCalendarInfo("");
    focusEventForm();
  }, [focusEventForm]);
  const canShareEvents = useMemo(
    () => hasPermission(user, CALENDAR_SHARE_PERMISSION),
    [user]
  );
  const [scheduleEditorTarget, setScheduleEditorTarget] = useState(null);
  const [scheduleEditorState, setScheduleEditorState] = useState(() => ({
    ...createEmptyScheduleState(),
    enabled: true,
  }));
  const [scheduleEditorError, setScheduleEditorError] = useState("");
  const [savingScheduleUpdate, setSavingScheduleUpdate] = useState(false);
  const [removingScheduleId, setRemovingScheduleId] = useState(null);

  const invitationStatusLabel = useCallback((status) => {
    if (status === "accepted") return "Aceito";
    if (status === "rejected") return "Recusado";
    return "Pendente";
  }, []);

  const receivedInvitations = useMemo(
    () => invitations.filter((invitation) => invitation.direction === "received"),
    [invitations]
  );
  const sentInvitations = useMemo(
    () => invitations.filter((invitation) => invitation.direction === "sent"),
    [invitations]
  );

  const loadCalendarSummary = useCallback(async () => {
    setCalendarLoading(true);
    setCalendarError("");
    setCalendarInfo("");
    try {
      const res = await api.get("/calendar/summary");
      const data = res.data || {};
      setCalendarSummary({
        events: Array.isArray(data.events) ? data.events : [],
        birthdays: Array.isArray(data.birthdays) ? data.birthdays : [],
        commemorativeDates: Array.isArray(data.commemorativeDates)
          ? data.commemorativeDates
          : [],
        integrations: Array.isArray(data.integrations)
          ? data.integrations
          : [],
        preferences: {
          ...DEFAULT_CALENDAR_PREFERENCES,
          ...(data.preferences || {}),
        },
        todayMessages: Array.isArray(data.todayMessages)
          ? data.todayMessages
          : [],
        birthdayGreeting: data.birthdayGreeting || null,
        schedules: Array.isArray(data.schedules) ? data.schedules : [],
      });
    } catch (error) {
      setCalendarError(
        error?.response?.data?.error || "Não foi possível carregar a agenda."
      );
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  const handleFetchCompletedSchedules = useCallback(async () => {
    if (!scheduleDateFilter.start || !scheduleDateFilter.end) {
      setCompletedError("Informe as datas inicial e final para consultar.");
      setCompletedSchedules([]);
      setCompletedRequested(false);
      return;
    }
    if (scheduleDateFilter.start > scheduleDateFilter.end) {
      setCompletedError("A data inicial não pode ser maior que a data final.");
      setCompletedSchedules([]);
      setCompletedRequested(false);
      return;
    }
    setCompletedLoading(true);
    setCompletedError("");
    try {
      const params = {
        startDate: scheduleDateFilter.start,
        endDate: scheduleDateFilter.end,
      };
      const response = await api.get("/calendar/schedules/completed", { params });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setCompletedSchedules(items);
      setCompletedRequested(true);
    } catch (error) {
      const message =
        error?.response?.data?.error || "Falha ao consultar execuções realizadas.";
      setCompletedError(message);
      setCompletedSchedules([]);
      setCompletedRequested(true);
    } finally {
      setCompletedLoading(false);
    }
  }, [scheduleDateFilter]);

  const resetEventForm = useCallback(() => {
    setEventForm(createEmptyEventForm());
  }, []);

  const assistantActionType = assistantAction?.type;
  const assistantActionKey = assistantAction?.key;
  const assistantActionPrefill = assistantAction?.prefill;

  useEffect(() => {
    if (assistantActionType !== "new-event") {
      return;
    }
    const sanitizedPrefill = sanitizeEventPrefill(assistantActionPrefill);
    const mergedForm = { ...createEmptyEventForm(), ...sanitizedPrefill };
    setEventForm(mergedForm);
    setCalendarError("");
    const highlights = describePrefillHighlights(sanitizedPrefill);
    if (highlights.length) {
      const summary =
        highlights.length === 1
          ? highlights[0]
          : `${highlights.slice(0, -1).join(", ")} e ${highlights[highlights.length - 1]}`;
      setCalendarInfo(
        `Preenchi ${summary} com base no seu comando. Ajuste os demais campos e confirme o evento.`
      );
    } else {
      setCalendarInfo("Deixei o formulário pronto para você cadastrar um novo evento.");
    }
    if (eventTitleInputRef.current) {
      eventTitleInputRef.current.focus();
    }
  }, [assistantActionKey, assistantActionPrefill, assistantActionType]);

  const handleEventInputChange = useCallback((field, value) => {
    setEventForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRecipientsMultiSelectChange = useCallback((field, options) => {
    const values = Array.from(options || [])
      .map((option) => Number(option.value))
      .filter((value) => Number.isInteger(value) && value > 0);
    const unique = Array.from(new Set(values));
    setEventForm((prev) => ({ ...prev, [field]: unique }));
  }, []);

  const handleRecipientDelete = useCallback((event, field) => {
    if (!["Delete", "Backspace"].includes(event.key)) return;
    event.preventDefault();
    setEventForm(prev => ({ ...prev, [field]: [] }));
  }, []);

  const handleToggleSendToAll = useCallback((checked) => {
    setEventForm((prev) => ({
      ...prev,
      sendToAll: checked,
      targetUserIds: checked ? [] : prev.targetUserIds,
      targetSectorIds: checked ? [] : prev.targetSectorIds,
    }));
  }, []);

  const handleEditEvent = useCallback((event) => {
    setCalendarModalOpen(false);
    setCalendarError("");
    focusEventForm();
    if (!event || event.source !== "local" || event.localId == null) {
      setCalendarInfo(
        "Eventos sincronizados devem ser editados diretamente no calendário de origem."
      );
      return;
    }
    let time = "";
    if (event.date) {
      const parsed = new Date(event.date);
      if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
        const hours = String(parsed.getHours()).padStart(2, "0");
        const minutes = String(parsed.getMinutes()).padStart(2, "0");
        time = `${hours}:${minutes}`;
      }
    }
    setEventForm({
      localId: event.localId,
      title: event.title || "",
      description: event.description || "",
      date: event.date ? event.date.slice(0, 10) : "",
      time: event.allDay ? "" : time,
      notifyOnDashboard: Boolean(event.notifyOnDashboard),
      targetUserIds: [],
      targetSectorIds: [],
      sendToAll: false,
      inviteUserIds: [],
      forceInvite: false,
    });
  }, [focusEventForm]);

  const conflictingEvent = useMemo(() => {
    const dateValue = (eventForm.date || "").trim();
    const timeValue = (eventForm.time || "").trim();
    if (!dateValue || !timeValue) return null;
    const targetDateTime = new Date(`${dateValue}T${timeValue}`);
    if (Number.isNaN(targetDateTime.getTime())) return null;
    const events = Array.isArray(calendarSummary.events)
      ? calendarSummary.events
      : [];
    return (
      events.find((event) => {
        if (!event?.date) return false;
        if (event?.allDay) return false;
        if (
          eventForm.localId != null &&
          event.source === "local" &&
          event.localId === eventForm.localId
        ) {
          return false;
        }
        const eventDate = new Date(event.date);
        if (Number.isNaN(eventDate.getTime())) return false;
        return (
          eventDate.getFullYear() === targetDateTime.getFullYear() &&
          eventDate.getMonth() === targetDateTime.getMonth() &&
          eventDate.getDate() === targetDateTime.getDate() &&
          eventDate.getHours() === targetDateTime.getHours() &&
          eventDate.getMinutes() === targetDateTime.getMinutes()
        );
      }) || null
    );
  }, [calendarSummary.events, eventForm.date, eventForm.time, eventForm.localId]);

  const handleSubmitEvent = useCallback(
    async (e) => {
      e.preventDefault();
      setCalendarError("");
      const title = eventForm.title.trim();
      if (!title) {
        setCalendarError("Informe um título para o evento.");
        return;
      }
      if (!eventForm.date) {
        setCalendarError("Informe uma data para o evento.");
        return;
      }
      let combinedDate = eventForm.date;
      let isAllDay = true;
      const timeValue = (eventForm.time || "").trim();
      if (timeValue) {
        const parsedDate = new Date(`${eventForm.date}T${timeValue}`);
        if (Number.isNaN(parsedDate.getTime())) {
          setCalendarError("Horário do evento inválido.");
          return;
        }
        combinedDate = parsedDate.toISOString();
        isAllDay = false;
      }
      setSavingEvent(true);
      const payload = {
        title,
        description: eventForm.description,
        date: combinedDate,
        allDay: isAllDay,
        notifyOnDashboard: Boolean(eventForm.notifyOnDashboard),
      };
      const editing = eventForm.localId != null;
      if (!editing) {
        const targetUserIds = (eventForm.targetUserIds || []).filter((value) =>
          Number.isInteger(value) && value > 0
        );
        const targetSectorIds = (eventForm.targetSectorIds || []).filter(
          (value) => Number.isInteger(value) && value > 0
        );
        const sendToAll = canShareEvents ? Boolean(eventForm.sendToAll) : false;
        const inviteUserIds = (eventForm.inviteUserIds || [])
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0);
        if (canShareEvents) {
          if (sendToAll) {
            payload.sendToAll = true;
          } else {
            if (targetUserIds.length > 0) {
              payload.targetUserIds = targetUserIds;
            }
            if (targetSectorIds.length > 0) {
              payload.targetSectorIds = targetSectorIds;
            }
          }
        }
        if (inviteUserIds.length > 0) {
          payload.inviteUserIds = inviteUserIds;
          if (isMasterUser && eventForm.forceInvite) {
            payload.forceInvite = true;
          }
        }
      }
      try {
        let successMessage = editing
          ? "Evento atualizado com sucesso!"
          : "Evento salvo com sucesso!";
        if (editing) {
          await api.put(`/calendar/events/${eventForm.localId}`, payload);
        } else {
          const response = await api.post(`/calendar/events`, payload);
          const sharedCount = Number(response?.data?.sharedWithCount ?? 0);
          if (sharedCount > 0) {
            successMessage = `Evento salvo e enviado para ${sharedCount} ${
              sharedCount === 1 ? "participante" : "participantes"
            }!`;
          }
          const invitationCount = Number(response?.data?.invitationCount ?? 0);
          const forcedInvitationCount = Number(response?.data?.forcedInvitationCount ?? 0);
          if (invitationCount > 0 && forcedInvitationCount === 0) {
            successMessage = `Evento criado e ${invitationCount} ${
              invitationCount === 1 ? "convite enviado" : "convites enviados"
            } para confirmação.`;
          }
          if (forcedInvitationCount > 0) {
            successMessage = `Evento criado e aplicado diretamente para ${forcedInvitationCount} ${
              forcedInvitationCount === 1 ? "participante" : "participantes"
            }.`;
          }
        }
        await loadCalendarSummary();
        try {
          const invitationResponse = await api.get("/calendar/invitations");
          setInvitations(Array.isArray(invitationResponse.data) ? invitationResponse.data : []);
        } catch (invitationError) {
          // noop
        }
        resetEventForm();
        setCalendarInfo(successMessage);
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível salvar o evento."
        );
      } finally {
        setSavingEvent(false);
      }
    },
    [eventForm, loadCalendarSummary, resetEventForm, canShareEvents, isMasterUser]
  );

  const handleDeleteEvent = useCallback(
    async (event) => {
      if (!event || event.source !== "local" || event.localId == null) {
        setCalendarInfo(
          "Eventos sincronizados devem ser excluídos diretamente no calendário de origem."
        );
        return;
      }
      const confirmed = window.confirm(
        `Remover o evento "${event.title}"? Essa ação não pode ser desfeita.`
      );
      if (!confirmed) return;
      setCalendarError("");
      setCalendarInfo("");
      try {
        await api.delete(`/calendar/events/${event.localId}`);
        await loadCalendarSummary();
        try {
          const invitationResponse = await api.get("/calendar/invitations");
          setInvitations(Array.isArray(invitationResponse.data) ? invitationResponse.data : []);
        } catch (invitationError) {
          // noop
        }
        resetEventForm();
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível remover o evento."
        );
      }
    },
    [loadCalendarSummary, resetEventForm]
  );

  const togglePreference = useCallback(
    async (key) => {
      setCalendarError("");
      setCalendarInfo("");
      setUpdatingPreferences(true);
      try {
        const current = Boolean(calendarSummary.preferences?.[key]);
        await api.put("/calendar/preferences", { [key]: !current });
        await loadCalendarSummary();
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível atualizar as preferências."
        );
      } finally {
        setUpdatingPreferences(false);
      }
    },
    [calendarSummary.preferences, loadCalendarSummary]
  );

  const handleIntegrationInputChange = useCallback((field, value) => {
    setIntegrationForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmitIntegration = useCallback(
    async (event) => {
      event.preventDefault();
      setCalendarError("");
      setCalendarInfo("");
      const sourceUrl = integrationForm.sourceUrl.trim();
      if (!sourceUrl) {
        setCalendarError("Informe o link de assinatura do calendário.");
        return;
      }
      setSavingIntegration(true);
      try {
        await api.post(`/calendar/integrations`, {
          provider: integrationForm.provider,
          sourceUrl,
          displayName: integrationForm.displayName.trim() || null,
        });
        await loadCalendarSummary();
        setIntegrationForm({ provider: "google", sourceUrl: "", displayName: "" });
        setCalendarInfo("Integração adicionada com sucesso!");
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível conectar o calendário."
        );
      } finally {
        setSavingIntegration(false);
      }
    },
    [integrationForm, loadCalendarSummary]
  );

  const handleUpdateIntegration = useCallback(
    async (integration) => {
      if (!integration) return;
      setCalendarError("");
      setCalendarInfo("");
      setUpdatingIntegrationIds((prev) =>
        prev.includes(integration.id) ? prev : [...prev, integration.id]
      );
      try {
        await api.put(`/calendar/integrations/${integration.id}`, {
          displayName: integration.displayName?.trim() || null,
        });
        await loadCalendarSummary();
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível atualizar a integração."
        );
      } finally {
        setUpdatingIntegrationIds((prev) =>
          prev.filter((value) => value !== integration.id)
        );
      }
    },
    [loadCalendarSummary]
  );

  const handleSyncIntegration = useCallback(
    async (integration) => {
      if (!integration) return;
      setCalendarError("");
      setCalendarInfo("");
      setSyncingIntegrationIds((prev) =>
        prev.includes(integration.id) ? prev : [...prev, integration.id]
      );
      try {
        await api.post(`/calendar/integrations/${integration.id}/sync`);
        await loadCalendarSummary();
        setCalendarInfo(
          `Sincronização com ${
            integration.displayName || integration.providerLabel || "integração"
          } concluída!`
        );
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível sincronizar o calendário conectado."
        );
      } finally {
        setSyncingIntegrationIds((prev) =>
          prev.filter((value) => value !== integration.id)
        );
      }
    },
    [loadCalendarSummary]
  );

  const handleDeleteIntegration = useCallback(
    async (integration) => {
      if (!integration) return;
      const confirmed = window.confirm(
        `Remover a conexão com ${integration.displayName || integration.providerLabel}?`
      );
      if (!confirmed) return;
      setCalendarError("");
      setCalendarInfo("");
      setRemovingIntegrationIds((prev) =>
        prev.includes(integration.id) ? prev : [...prev, integration.id]
      );
      try {
        await api.delete(`/calendar/integrations/${integration.id}`);
        await loadCalendarSummary();
      } catch (error) {
        setCalendarError(
          error?.response?.data?.error || "Não foi possível remover a integração."
        );
      } finally {
        setRemovingIntegrationIds((prev) =>
          prev.filter((value) => value !== integration.id)
        );
      }
    },
    [loadCalendarSummary]
  );

  const openScheduleEditor = useCallback((schedule) => {
    if (!schedule) return;
    setScheduleEditorTarget(schedule);
    setScheduleEditorState({
      ...createStateFromSchedule(schedule),
      enabled: true,
    });
    setScheduleEditorError("");
    setScheduleEditorOpen(true);
  }, []);

  const closeScheduleEditor = useCallback(() => {
    setScheduleEditorOpen(false);
    setScheduleEditorTarget(null);
    setScheduleEditorState({
      ...createEmptyScheduleState(),
      enabled: true,
    });
    setScheduleEditorError("");
  }, []);

  const handleScheduleUpdate = useCallback(async () => {
    if (!scheduleEditorTarget) return;
    const validation = buildScheduleRequest(scheduleEditorState);
    if (!validation.valid || !validation.payload) {
      setScheduleEditorError(
        validation.message || "Preencha os dados do agendamento para continuar."
      );
      return;
    }
    setSavingScheduleUpdate(true);
    setCalendarError("");
    setCalendarInfo("");
    try {
      await api.put(`/processes/schedules/${scheduleEditorTarget.id}`, validation.payload);
      await loadCalendarSummary();
      setCalendarInfo("Agendamento atualizado com sucesso!");
      closeScheduleEditor();
    } catch (error) {
      const message =
        error?.response?.data?.error || "Não foi possível atualizar o agendamento.";
      setScheduleEditorError(message);
    } finally {
      setSavingScheduleUpdate(false);
    }
  }, [scheduleEditorTarget, scheduleEditorState, closeScheduleEditor, loadCalendarSummary]);

  const handleDeleteSchedule = useCallback(
    async (schedule) => {
      if (!schedule) return;
      const confirmed = window.confirm(
        `Cancelar o agendamento do processo "${schedule.processName}"?`
      );
      if (!confirmed) return;
      setCalendarError("");
      setCalendarInfo("");
      setRemovingScheduleId(schedule.id);
      try {
        await api.delete(`/processes/schedules/${schedule.id}`);
        await loadCalendarSummary();
        setCalendarInfo("Agendamento removido com sucesso.");
        if (scheduleEditorTarget?.id === schedule.id) {
          closeScheduleEditor();
        }
      } catch (error) {
        const message =
          error?.response?.data?.error || "Não foi possível remover o agendamento.";
        setCalendarError(message);
      } finally {
        setRemovingScheduleId(null);
      }
    },
    [closeScheduleEditor, loadCalendarSummary, scheduleEditorTarget]
  );

  const parseAsLocalDate = useCallback((value) => {
    if (!value) return null;
    if (typeof value === "string") {
      const datePart = value.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split("-").map((part) => Number(part));
        if (![year, month, day].some((part) => Number.isNaN(part))) {
          return new Date(year, month - 1, day);
        }
      }
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const parseFilterDate = useCallback((value, endOfDay = false) => {
    if (!value) return null;
    const normalized = `${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }, []);

  const buildDateKey = useCallback((date) => {
    if (!(date instanceof Date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const formatDateLabel = useCallback(
    (iso) => {
      const date = parseAsLocalDate(iso);
      if (!date) return "-";
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    [parseAsLocalDate]
  );

  const formatDateTimeLabel = useCallback((iso) => {
    if (!iso) return "-";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const formatTimeLabel = useCallback((iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const describeDaysUntil = useCallback((days) => {
    if (days === 0) return "Hoje";
    if (days === 1) return "Amanhã";
    if (days > 1) return `Em ${days} dias`;
    if (days === -1) return "Ontem";
    return `${Math.abs(days)} dias atrás`;
  }, []);

  const todayDate = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const aggregatedCalendarItems = useMemo(() => {
    const map = new Map();
    const addItem = (date, item) => {
      if (!date) return;
      const key = buildDateKey(date);
      if (!key) return;
      const items = map.get(key) || [];
      items.push(item);
      map.set(key, items);
    };

    (calendarSummary.events || []).forEach((event) => {
      const date = parseAsLocalDate(event.date);
      if (!date) return;
      const isAllDay =
        event?.allDay === undefined || event?.allDay === null
          ? true
          : Boolean(event.allDay);
      const timeLabel = isAllDay ? "" : formatTimeLabel(event.date);
      addItem(date, {
        type: "event",
        event,
        label: event.title || "Evento",
        description: event.description || "",
        source: event.source,
        allDay: isAllDay,
        timeLabel,
      });
    });

    (calendarSummary.birthdays || []).forEach((birthday) => {
      const date = parseAsLocalDate(birthday.date);
      if (!date) return;
      addItem(date, {
        type: "birthday",
        label: birthday.name,
        turningAge: birthday.turningAge,
      });
    });

    (calendarSummary.commemorativeDates || []).forEach((item) => {
      const date = parseAsLocalDate(item.date);
      if (!date) return;
      addItem(date, {
        type: "commemorative",
        label: item.label,
      });
    });

    const typeOrder = { event: 0, birthday: 1, commemorative: 2 };
    const sorted = new Map();
    map.forEach((items, key) => {
      const ordered = items
        .slice()
        .sort((a, b) => {
          const firstType = typeOrder[a.type] ?? 0;
          const secondType = typeOrder[b.type] ?? 0;
          if (firstType !== secondType) return firstType - secondType;
          const firstLabel = (a.label || "").toString();
          const secondLabel = (b.label || "").toString();
          return firstLabel.localeCompare(secondLabel, "pt-BR");
        });
      sorted.set(key, ordered);
    });

    return sorted;
  }, [
    calendarSummary.events,
    calendarSummary.birthdays,
    calendarSummary.commemorativeDates,
    parseAsLocalDate,
    buildDateKey,
    formatTimeLabel,
  ]);

  const calendarWeeks = useMemo(() => {
    const view = calendarViewDate;
    if (!view) return [];
    const start = new Date(view.getFullYear(), view.getMonth(), 1);
    const startDay = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - startDay);
    const weeks = [];
    const cursor = new Date(gridStart);
    for (let week = 0; week < 6; week += 1) {
      const days = [];
      for (let day = 0; day < 7; day += 1) {
        const cellDate = new Date(cursor);
        const key = buildDateKey(cellDate);
        const items = aggregatedCalendarItems.get(key) || [];
        days.push({
          date: cellDate,
          key,
          inMonth: cellDate.getMonth() === view.getMonth(),
          isToday: buildDateKey(cellDate) === buildDateKey(todayDate),
          items,
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(days);
    }
    return weeks;
  }, [aggregatedCalendarItems, buildDateKey, calendarViewDate, todayDate]);

  const calendarMonthLabel = useMemo(() => {
    const label = calendarViewDate.toLocaleDateString("pt-BR", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [calendarViewDate]);

  const openCalendarModal = useCallback(() => {
    const now = new Date();
    setCalendarViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setCalendarModalOpen(true);
  }, []);

  const closeCalendarModal = useCallback(() => {
    setCalendarModalOpen(false);
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setCalendarViewDate((prev) => {
      const base = prev ? new Date(prev) : new Date();
      return new Date(base.getFullYear(), base.getMonth() - 1, 1);
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setCalendarViewDate((prev) => {
      const base = prev ? new Date(prev) : new Date();
      return new Date(base.getFullYear(), base.getMonth() + 1, 1);
    });
  }, []);

  const goToToday = useCallback(() => {
    const now = new Date();
    setCalendarViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const sortedEvents = useMemo(() => {
    const events = Array.isArray(calendarSummary.events)
      ? calendarSummary.events
      : [];
    const today = todayDate;
    const withMetadata = events.map((event) => {
      const parsed = parseAsLocalDate(event.date) || new Date(event.date);
      const isValidDate =
        parsed instanceof Date && !Number.isNaN(parsed.getTime());
      const normalizedDate = isValidDate
        ? new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
        : null;
      let daysUntil =
        typeof event.daysUntil === "number" && Number.isFinite(event.daysUntil)
          ? Math.trunc(event.daysUntil)
          : null;
      if (
        daysUntil === null &&
        normalizedDate &&
        today instanceof Date &&
        !Number.isNaN(today.getTime())
      ) {
        const diffMs = normalizedDate.getTime() - today.getTime();
        daysUntil = Math.round(diffMs / 86400000);
      }
      return {
        ...event,
        daysUntil,
        _normalizedDate: normalizedDate,
      };
    });

    withMetadata.sort((a, b) => {
      const first = a._normalizedDate;
      const second = b._normalizedDate;
      if (first && second) {
        return first.getTime() - second.getTime();
      }
      if (first) return -1;
      if (second) return 1;
      return 0;
    });

    return withMetadata.map((event) => {
      const { _normalizedDate, ...rest } = event;
      return rest;
    });
  }, [calendarSummary.events, parseAsLocalDate, todayDate]);

  const localEvents = useMemo(
    () => sortedEvents.filter((event) => event.source !== "integration"),
    [sortedEvents]
  );

  const integrationEvents = useMemo(
    () => sortedEvents.filter((event) => event.source === "integration"),
    [sortedEvents]
  );

  const upcomingLocalEvents = useMemo(
    () => localEvents.filter((event) => event.daysUntil >= 0),
    [localEvents]
  );

  const pastLocalEvents = useMemo(() => {
    const past = localEvents.filter((event) => event.daysUntil < 0);
    if (!past.length) return [];
    const start = Math.max(past.length - 3, 0);
    return past.slice(start).reverse();
  }, [localEvents]);

  const upcomingIntegrationEvents = useMemo(
    () => integrationEvents.filter((event) => event.daysUntil >= 0),
    [integrationEvents]
  );

  const nextBirthdays = useMemo(() => {
    return (calendarSummary.birthdays || [])
      .slice()
      .sort((a, b) => {
        const first = parseAsLocalDate(a.date) || new Date(a.date);
        const second = parseAsLocalDate(b.date) || new Date(b.date);
        return first.getTime() - second.getTime();
      })
      .slice(0, 6);
  }, [calendarSummary.birthdays, parseAsLocalDate]);

  const highlightedCommemorativeDates = useMemo(() => {
    return (calendarSummary.commemorativeDates || [])
      .slice()
      .sort((a, b) => {
        const first = parseAsLocalDate(a.date) || new Date(a.date);
        const second = parseAsLocalDate(b.date) || new Date(b.date);
        return first.getTime() - second.getTime();
      })
      .slice(0, 6);
  }, [calendarSummary.commemorativeDates, parseAsLocalDate]);

  const connectedIntegrations = useMemo(() => {
    return Array.isArray(calendarSummary.integrations)
      ? calendarSummary.integrations
      : [];
  }, [calendarSummary.integrations]);

  const activeSchedules = useMemo(
    () => (Array.isArray(calendarSummary.schedules) ? calendarSummary.schedules : []),
    [calendarSummary.schedules]
  );

  const scheduleItems = useMemo(
    () => (scheduleTimeFilter === "completed" ? completedSchedules : activeSchedules),
    [scheduleTimeFilter, completedSchedules, activeSchedules]
  );

  const isLoadingSchedules =
    scheduleTimeFilter === "completed" ? completedLoading : calendarLoading;
  const scheduleLoadingMessage =
    scheduleTimeFilter === "completed"
      ? "Consultando execuções realizadas..."
      : "Carregando agendamentos...";

  const scheduleDateRange = useMemo(() => {
    return {
      start: parseFilterDate(scheduleDateFilter.start, false),
      end: parseFilterDate(scheduleDateFilter.end, true),
    };
  }, [scheduleDateFilter, parseFilterDate]);

  const visibleSchedules = useMemo(() => {
    if (scheduleTimeFilter === "completed") {
      return scheduleItems;
    }
    return scheduleItems.filter((item) => item.active);
  }, [scheduleItems, scheduleTimeFilter]);

  const getScheduleReferenceDate = useCallback(
    (schedule) => {
      const rawDate =
        scheduleTimeFilter === "completed"
          ? schedule?.lastResult?.ranAt || schedule?.nextRunAt
          : schedule?.nextRunAt || schedule?.lastResult?.ranAt;
      if (!rawDate) return null;
      const reference = new Date(rawDate);
      return Number.isNaN(reference.getTime()) ? null : reference;
    },
    [scheduleTimeFilter]
  );

  const dateFilteredSchedules = useMemo(() => {
    if (scheduleTimeFilter === "completed") {
      return visibleSchedules;
    }
    const { start, end } = scheduleDateRange;
    if (!start && !end) return visibleSchedules;
    return visibleSchedules.filter((item) => {
      const referenceDate = getScheduleReferenceDate(item);
      if (!referenceDate) return false;
      if (start && referenceDate.getTime() < start.getTime()) return false;
      if (end && referenceDate.getTime() > end.getTime()) return false;
      return true;
    });
  }, [visibleSchedules, scheduleDateRange, getScheduleReferenceDate, scheduleTimeFilter]);

  const hasScheduleDateFilter = useMemo(
    () => Boolean(scheduleDateFilter.start || scheduleDateFilter.end),
    [scheduleDateFilter]
  );

  const scheduleCounts = useMemo(() => {
    const mine = dateFilteredSchedules.filter((item) => item.createdByMe).length;
    const incoming = dateFilteredSchedules.filter((item) => item.willAssignToMe).length;
    return {
      all: dateFilteredSchedules.length,
      mine,
      incoming,
    };
  }, [dateFilteredSchedules]);

  const scheduleTimeFilterOptions = useMemo(
    () => [
      { value: "upcoming", label: "Próximas execuções" },
      { value: "completed", label: "Execuções realizadas" },
    ],
    []
  );

  const scheduleFilterOptions = useMemo(
    () => [
      { value: "all", label: "Todos", count: scheduleCounts.all },
      { value: "mine", label: "Agendados por mim", count: scheduleCounts.mine },
      { value: "incoming", label: "Chegam para mim", count: scheduleCounts.incoming },
    ],
    [scheduleCounts]
  );

  const filteredSchedules = useMemo(() => {
    if (scheduleFilter === "mine") {
      return dateFilteredSchedules.filter((item) => item.createdByMe);
    }
    if (scheduleFilter === "incoming") {
      return dateFilteredSchedules.filter((item) => item.willAssignToMe);
    }
    return dateFilteredSchedules;
  }, [scheduleFilter, dateFilteredSchedules]);

  const scheduleEmptyMessage = useMemo(() => {
    if (scheduleTimeFilter === "completed" && !completedRequested) {
      return "Informe o período desejado e clique em Consultar para visualizar execuções realizadas.";
    }
    if (scheduleTimeFilter === "completed" && completedLoading) {
      return "Consultando execuções realizadas...";
    }
    if (hasScheduleDateFilter) {
      return "Nenhum agendamento encontrado para o período selecionado.";
    }
    if (scheduleTimeFilter === "completed") {
      return "Nenhuma execução agendada foi realizada até o momento.";
    }
    if (scheduleFilter === "mine") {
      return "Você ainda não programou solicitações.";
    }
    if (scheduleFilter === "incoming") {
      return "Nenhuma solicitação agendada chegará para você.";
    }
    return "Nenhum agendamento foi encontrado.";
  }, [
    hasScheduleDateFilter,
    scheduleFilter,
    scheduleTimeFilter,
    completedRequested,
    completedLoading,
  ]);

  const describeRecurring = useCallback((schedule) => {
    if (!schedule?.config) return null;
    const { config } = schedule;
    const weekdayValues = Array.isArray(config.weekdays) ? config.weekdays : [];
    const uniqueWeekdays = Array.from(
      new Set(
        weekdayValues
          .map((value) => Number(value))
          .filter((day) => !Number.isNaN(day) && day >= 0 && day <= 6)
      )
    ).sort((a, b) => a - b);
    const weekdayLabel = uniqueWeekdays.length
      ? uniqueWeekdays.map((day) => WEEKDAY_SHORT[day]).join(", ")
      : null;

    const timeValues = Array.isArray(config.times) ? config.times : [];
    const uniqueTimes = Array.from(
      new Set(
        timeValues
          .map((value) => {
            if (!value) return null;
            const match = `${value}`.match(/^(\d{1,2}):(\d{2})$/);
            if (!match) return null;
            const hour = Number(match[1]);
            const minute = Number(match[2]);
            if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
            if (hour < 0 || hour > 23) return null;
            if (minute < 0 || minute > 59) return null;
            return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
          })
          .filter(Boolean)
      )
    );
    const timeLabel = uniqueTimes.length ? uniqueTimes.join(", ") : null;

    if (weekdayLabel && timeLabel) {
      return `${weekdayLabel} às ${timeLabel}`;
    }
    if (weekdayLabel) return weekdayLabel;
    if (timeLabel) return `Horários: ${timeLabel}`;
    return null;
  }, []);

  const describeMonthly = useCallback((schedule) => {
    if (!schedule?.config) return null;
    const dayOfMonth = Math.trunc(Number(schedule.config.dayOfMonth));
    const time = typeof schedule.config.time === "string"
      ? schedule.config.time.slice(0, 5)
      : "";
    if (!Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
      return null;
    }
    return time
      ? `Todo mês, no dia ${dayOfMonth}, às ${time}`
      : `Todo mês, no dia ${dayOfMonth}`;
  }, []);

  const isEditingEvent = Boolean(eventForm.localId);
  const calendarPreferences =
    calendarSummary.preferences || DEFAULT_CALENDAR_PREFERENCES;
  const showEventsPanel = Boolean(calendarPreferences.showEventsOnDashboard);
  const showBirthdaysPanel = Boolean(
    calendarPreferences.showBirthdaysOnDashboard
  );
  const showCommemorativePanel = Boolean(
    calendarPreferences.showCommemorativeDates
  );

  useEffect(() => {
    loadCalendarSummary();
  }, [loadCalendarSummary, refreshKey]);

  useEffect(() => {
    let active = true;
    const loadRecipients = async () => {
      setRecipientLoading(true);
      setRecipientError("");
      try {
        const response = await api.get("/calendar/recipients");
        if (!active) return;
        const rawUsers = Array.isArray(response.data?.users)
          ? response.data.users
          : [];
        const rawSectors = Array.isArray(response.data?.sectors)
          ? response.data.sectors
          : [];
        const formattedUsers = rawUsers
          .map((item) => {
            const id = Number(item?.id ?? item?.userId ?? item?.value);
            const name =
              typeof item?.name === "string" && item.name.trim()
                ? item.name.trim()
                : "Usuário sem nome";
            const sectorName =
              typeof item?.sectorName === "string" && item.sectorName.trim()
                ? item.sectorName.trim()
                : typeof item?.sector?.name === "string" && item.sector.name.trim()
                ? item.sector.name.trim()
                : null;
            return { id, name, sectorName };
          })
          .filter((userOption) => Number.isInteger(userOption.id) && userOption.id > 0);
        const formattedSectors = rawSectors
          .map((item) => {
            const id = Number(item?.id ?? item?.value);
            const name =
              typeof item?.name === "string" && item.name.trim()
                ? item.name.trim()
                : "Setor";
            return { id, name };
          })
          .filter((sectorOption) => Number.isInteger(sectorOption.id) && sectorOption.id > 0);
        setRecipientOptions({
          users: formattedUsers,
          sectors: formattedSectors,
        });
      } catch (error) {
        if (!active) return;
        setRecipientError(
          error?.response?.data?.error ||
            "Não foi possível carregar os destinatários disponíveis."
        );
        setRecipientOptions({ users: [], sectors: [] });
      } finally {
        if (active) {
          setRecipientLoading(false);
        }
      }
    };
    loadRecipients();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  useEffect(() => {
    let active = true;
    const loadInvitations = async () => {
      setInvitationLoading(true);
      try {
        const response = await api.get("/calendar/invitations");
        if (!active) return;
        setInvitations(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (!active) return;
        setInvitations([]);
      } finally {
        if (active) setInvitationLoading(false);
      }
    };
    loadInvitations();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const handleInvitationResponse = useCallback(
    async (invitationId, action) => {
      try {
        await api.post(`/calendar/invitations/${invitationId}/respond`, { action });
        const response = await api.get("/calendar/invitations");
        setInvitations(Array.isArray(response.data) ? response.data : []);
        await loadCalendarSummary();
      } catch (error) {
        setCalendarError(error?.response?.data?.error || "Não foi possível responder ao convite.");
      }
    },
    [loadCalendarSummary]
  );

  useEffect(() => {
    if (scheduleTimeFilter !== "completed") return;
    setCompletedRequested(false);
    setCompletedSchedules([]);
    setCompletedError("");
  }, [scheduleDateFilter.start, scheduleDateFilter.end, scheduleTimeFilter]);

  return (
    <div className="agenda-polish space-y-6 bg-white px-6 py-6 text-slate-800">
      <header className="agenda-polish__hero relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-[#07174c] via-[#061136] to-[#050a21] p-6 text-white shadow-xl">
        <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-blue-400/20 blur-3xl" />
        <div className="absolute -bottom-12 left-1/3 h-32 w-32 rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.45em] text-blue-200/80">
              integrada
            </p>
            <h1 className="mt-2 text-3xl font-semibold">Agenda</h1>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs sm:min-w-[360px]">
            <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur">
              <strong className="block text-lg text-white">{upcomingLocalEvents.length}</strong>
              Eventos
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur">
              <strong className="block text-lg text-white">{nextBirthdays.length}</strong>
              Aniversários
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur">
              <strong className="block text-lg text-white">{activeSchedules.length}</strong>
              Agendados
            </div>
          </div>
        </div>
      </header>

      {calendarSummary.birthdayGreeting && (
        <div className={`${agendaTab === "reminders" ? "" : "hidden "}rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-emerald-700 shadow-sm`}>
          {calendarSummary.birthdayGreeting}
        </div>
      )}

      {calendarSummary.todayMessages.length > 0 && (
        <div className={`${agendaTab === "reminders" ? "" : "hidden "}rounded-2xl border border-blue-100 bg-blue-50 px-5 py-4 text-sm text-[#0b2a63] shadow-sm`}>
          <p className="flex items-center gap-2 font-medium text-[#0b2a63]">
            <FaBell className="text-base" />
            Lembretes do dia
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {calendarSummary.todayMessages.map((message, index) => (
              <li key={`${message}-${index}`}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {(invitationLoading || receivedInvitations.length > 0 || sentInvitations.length > 0) && (
        <section className={`${agendaTab === "reminders" ? "" : "hidden "}rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 shadow-sm`}>
          <div className="flex items-center gap-2 text-emerald-700">
            <FaUserCheck />
            <h2 className="text-sm font-semibold">Convites</h2>
          </div>
          {invitationLoading ? (
            <p className="mt-2 text-xs text-emerald-700">Carregando...</p>
          ) : (
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {receivedInvitations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Recebidos</h3>
                  {receivedInvitations.map((invitation) => (
                    <div key={`received-${invitation.id}`} className="rounded-2xl border border-emerald-200 bg-white/90 p-3 shadow-sm">
                      <p className="text-sm text-slate-700">
                        <strong>{invitation.creatorUserName}</strong> · {invitation.event?.title || "Evento"}
                      </p>
                      {invitation.event?.date && (
                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                          <FaCalendarAlt aria-hidden="true" />
                          {invitation.event?.allDay === true
                            ? formatDateLabel(invitation.event.date)
                            : formatDateTimeLabel(invitation.event.date)}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">{invitationStatusLabel(invitation.status)}</p>
                      {invitation.status === "pending" && (
                        <div className="mt-2 flex gap-2">
                          <button type="button" onClick={() => handleInvitationResponse(invitation.id, "accepted")} className="rounded-full bg-emerald-600 px-3 py-1 text-xs text-white">Aceitar</button>
                          <button type="button" onClick={() => handleInvitationResponse(invitation.id, "rejected")} className="rounded-full border border-rose-200 px-3 py-1 text-xs text-rose-600">Recusar</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {sentInvitations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Enviados</h3>
                  {sentInvitations.map((invitation) => (
                    <div key={`sent-${invitation.id}`} className="rounded-2xl border border-emerald-200 bg-white/90 p-3 shadow-sm">
                      <p className="text-sm text-slate-700">
                        <strong>{invitation.invitedUserName}</strong> · {invitation.event?.title || "Evento"}
                      </p>
                      {invitation.event?.date && (
                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                          <FaCalendarAlt aria-hidden="true" />
                          {invitation.event?.allDay === true
                            ? formatDateLabel(invitation.event.date)
                            : formatDateTimeLabel(invitation.event.date)}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">{invitationStatusLabel(invitation.status)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => setAgendaTab("reminders")}
            className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${agendaTab === "reminders" ? "bg-[#0b2a63] text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <span className="inline-flex items-center gap-2"><FaBell /> Lembretes e eventos</span>
          </button>
          <button
            type="button"
            onClick={() => setAgendaTab("schedules")}
            className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${agendaTab === "schedules" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <span className="inline-flex items-center gap-2"><FaCalendarCheck /> Solicitações agendadas</span>
          </button>
        </div>
      </div>

      <section className={`${agendaTab === "reminders" ? "" : "hidden "}rounded-[2rem] border border-blue-100 bg-gradient-to-br from-white via-blue-50/50 to-indigo-50 p-6 shadow-sm`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Lembretes
            </h2>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {[
              {
                key: "showEventsOnDashboard",
                label: "Eventos no dashboard",
                icon: <FaCalendarAlt className="text-sm" />,
              },
              {
                key: "showBirthdaysOnDashboard",
                label: "Aniversários",
                icon: <FaBirthdayCake className="text-sm" />,
              },
              {
                key: "showCommemorativeDates",
                label: "Datas comemorativas",
                icon: <FaBell className="text-sm" />,
              },
            ].map((option) => {
              const active = Boolean(calendarSummary.preferences?.[option.key]);
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => togglePreference(option.key)}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition ${
                    active
                      ? "bg-[#0b2a63] text-white shadow"
                      : "border border-blue-200 text-[#0b2a63] hover:bg-blue-50"
                  }`}
                  disabled={updatingPreferences}
                >
                  {option.icon}
                  {option.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={openCalendarModal}
              className="flex items-center gap-2 rounded-full border border-blue-200 px-4 py-2 text-xs font-medium text-[#0b2a63] transition hover:bg-blue-50"
            >
              <FaCalendarCheck className="text-sm" />
              Abrir calendário
            </button>
          </div>
        </div>

        {calendarError && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {calendarError}
          </div>
        )}

        {calendarInfo && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {calendarInfo}
          </div>
        )}

        <div className="mt-6">
          <AgendaInlineCalendar
            weeks={calendarWeeks}
            monthLabel={calendarMonthLabel}
            onPrevious={goToPreviousMonth}
            onNext={goToNextMonth}
            onToday={goToToday}
            onOpenFull={openCalendarModal}
            onSelectDay={handleSelectCalendarDay}
            onSelectEvent={handleEditEvent}
          />
        </div>

        <div className="mt-6">
          {calendarLoading ? (
            <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-6 text-center text-[#174a91]">
              Carregando sua agenda personalizada...
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              <form
                ref={eventFormRef}
                onSubmit={handleSubmitEvent}
                className="relative space-y-4 rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-inner"
                aria-busy={savingEvent}
              >
                {savingEvent && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80 text-[#0b2a63] backdrop-blur-sm"
                  >
                    <FaBell className="text-2xl animate-pulse" aria-hidden="true" />
                    <p className="text-sm font-semibold">Enviando notificações...</p>
                    <p className="px-4 text-center text-xs text-blue-500">
                      Aguarde um instante enquanto finalizamos o envio para os destinatários selecionados.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2 text-[#0b2a63]">
                  <FaCalendarPlus className="text-lg" />
                  <h3 className="text-base font-semibold">
                    {isEditingEvent ? "Editar evento" : "Novo evento"}
                  </h3>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                    Título
                  </label>
                  <input
                    type="text"
                    value={eventForm.title}
                    ref={eventTitleInputRef}
                    onChange={(e) => handleEventInputChange("title", e.target.value)}
                    className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Ex.: Reunião com fornecedores"
                    disabled={savingEvent}
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                    Data
                  </label>
                  <input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => handleEventInputChange("date", e.target.value)}
                    className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    disabled={savingEvent}
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                    Horário (opcional)
                  </label>
                  <input
                    type="time"
                    value={eventForm.time}
                    onChange={(e) => handleEventInputChange("time", e.target.value)}
                    className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    disabled={savingEvent}
                  />
                  <p className="text-[11px] text-slate-500">
                    Deixe em branco para criar um lembrete de dia inteiro.
                  </p>
                  {conflictingEvent && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                      Atenção: já existe o evento <strong>{conflictingEvent.title || "Evento"}</strong>{" "}
                      agendado para este mesmo horário.
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                    Observações
                  </label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => handleEventInputChange("description", e.target.value)}
                    className="h-24 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Detalhes, links ou contatos importantes"
                    disabled={savingEvent}
                  />
                </div>

                <label className="flex items-center gap-2 text-xs font-medium text-[#0b2a63]">
                  <input
                    type="checkbox"
                    checked={Boolean(eventForm.notifyOnDashboard)}
                    onChange={(e) =>
                      handleEventInputChange("notifyOnDashboard", e.target.checked)
                    }
                    disabled={savingEvent}
                    className="h-4 w-4 rounded border-blue-300 text-[#174a91] focus:ring-blue-400"
                  />
                  Notificar no dashboard
                </label>

                {(
                  <div className="space-y-3 rounded-xl border border-blue-100 bg-white/80 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#0b2a63]">
                      <FaUsers className="text-base" />
                      Compartilhar evento
                    </div>
                    {isEditingEvent ? (
                      <p className="text-xs text-[#174a91]">
                        O compartilhamento é configurado apenas ao criar um novo evento.
                      </p>
                    ) : (
                      <>
                        {canShareEvents && (
                        <label className="flex items-center gap-2 text-xs font-medium text-[#0b2a63]">
                          <input
                            type="checkbox"
                            checked={Boolean(eventForm.sendToAll)}
                            onChange={(e) => handleToggleSendToAll(e.target.checked)}
                            disabled={savingEvent || recipientLoading}
                            className="h-4 w-4 rounded border-blue-300 text-[#174a91] focus:ring-blue-400"
                          />
                          Enviar para todos os usuários cadastrados
                        </label>
                        )}
                        <div className="space-y-1">
                          <label className="text-[11px] font-medium uppercase tracking-wide text-[#174a91]">Convidar usuários (1 ou mais)</label>
                          <select
                            multiple
                            value={eventForm.inviteUserIds.map(String)}
                            onKeyDown={event => handleRecipientDelete(event, "inviteUserIds")}
                            onChange={(event) =>
                              handleRecipientsMultiSelectChange(
                                "inviteUserIds",
                                event.target.selectedOptions
                              )
                            }
                            disabled={savingEvent || recipientLoading || eventForm.sendToAll}
                            className="h-28 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
                          >
                            {recipientOptions.users.map((userOption) => (
                              <option key={`invite-${userOption.id}`} value={userOption.id}>
                                {userOption.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-[11px] text-slate-500">Use Ctrl/Command para selecionar mais de um usuário.</p>
                          {isMasterUser && (
                            <label className="flex items-center gap-2 text-xs font-medium text-[#0b2a63]">
                              <input
                                type="checkbox"
                                checked={Boolean(eventForm.forceInvite)}
                                onChange={(event) => handleEventInputChange("forceInvite", event.target.checked)}
                                disabled={savingEvent || !(eventForm.inviteUserIds || []).length}
                                className="h-4 w-4 rounded border-blue-300 text-[#174a91] focus:ring-blue-400"
                              />
                              Forçar evento para o usuário convidado (somente líder)
                            </label>
                          )}
                        </div>
                        {canShareEvents && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-[#174a91]">
                              Usuários
                            </label>
                            <select
                              multiple
                              value={eventForm.targetUserIds.map(String)}
                            onKeyDown={event => handleRecipientDelete(event, "targetUserIds")}
                              onChange={(event) =>
                                handleRecipientsMultiSelectChange(
                                  "targetUserIds",
                                  event.target.selectedOptions
                                )
                              }
                              disabled={
                                savingEvent ||
                                recipientLoading ||
                                eventForm.sendToAll
                              }
                              className="h-28 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            >
                              {recipientOptions.users.length ? (
                                recipientOptions.users.map((userOption) => (
                                  <option key={userOption.id} value={userOption.id}>
                                    {userOption.name}
                                    {userOption.sectorName
                                      ? ` · ${userOption.sectorName}`
                                      : ""}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>
                                  {recipientLoading
                                    ? "Carregando usuários..."
                                    : "Nenhum usuário encontrado"}
                                </option>
                              )}
                            </select>
                            <p className="text-[11px] text-slate-500">
                              Utilize Ctrl (Windows) ou Command (Mac) para escolher mais de um nome.
                            </p>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-[#174a91]">
                              Setores
                            </label>
                            <select
                              multiple
                              value={eventForm.targetSectorIds.map(String)}
                            onKeyDown={event => handleRecipientDelete(event, "targetSectorIds")}
                              onChange={(event) =>
                                handleRecipientsMultiSelectChange(
                                  "targetSectorIds",
                                  event.target.selectedOptions
                                )
                              }
                              disabled={
                                savingEvent ||
                                recipientLoading ||
                                eventForm.sendToAll
                              }
                              className="h-28 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            >
                              {recipientOptions.sectors.length ? (
                                recipientOptions.sectors.map((sectorOption) => (
                                  <option key={sectorOption.id} value={sectorOption.id}>
                                    {sectorOption.name}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>
                                  {recipientLoading
                                    ? "Carregando setores..."
                                    : "Nenhum setor encontrado"}
                                </option>
                              )}
                            </select>
                            <p className="text-[11px] text-slate-500">
                              Os usuários dos setores selecionados receberão uma cópia pessoal do evento.
                            </p>
                          </div>
                        </div>
                        )}
                        {canShareEvents && recipientError && (
                          <p className="text-xs text-rose-600">{recipientError}</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-70"
                    disabled={savingEvent}
                  >
                    {isEditingEvent ? "Atualizar evento" : "Salvar evento"}
                  </button>
                  {isEditingEvent && (
                    <button
                      type="button"
                      onClick={resetEventForm}
                      className="rounded-full border border-blue-200 px-4 py-2 text-sm font-medium text-[#174a91] transition hover:bg-blue-50"
                      disabled={savingEvent}
                    >
                      Cancelar edição
                    </button>
                  )}
                </div>

                <p className="text-xs text-[#0b2a63]">
                  Os lembretes são pessoais e aparecem somente para você.
                </p>
              </form>

              <div className="lg:col-span-2 space-y-6">
                {showEventsPanel && (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-slate-400">
                            próximos eventos
                          </p>
                        <h3 className="text-lg font-semibold text-slate-900">
                          Sua programação
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => togglePreference("showEventsOnDashboard")}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                        disabled={updatingPreferences}
                      >
                        {calendarPreferences.showEventsOnDashboard ? (
                          <FaToggleOn className="text-lg text-emerald-500" />
                        ) : (
                          <FaToggleOff className="text-lg text-slate-400" />
                        )}
                        Mostrar no dashboard
                      </button>
                    </div>

                    {upcomingLocalEvents.length ? (
                      <div className="mt-4 space-y-4">
                        {upcomingLocalEvents.map((event) => {
                          const isAllDay =
                            event?.allDay === undefined || event?.allDay === null
                              ? true
                              : Boolean(event.allDay);
                          return (
                            <div
                              key={event.id}
                              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600"
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <p className="text-xs uppercase tracking-[0.35em] text-slate-400">
                                    {describeDaysUntil(event.daysUntil)}
                                  </p>
                                  <h4 className="text-base font-semibold text-slate-900">
                                    {event.title}
                                  </h4>
                                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                                    {isAllDay ? (
                                      <>
                                        <FaCalendarAlt className="text-slate-400" aria-hidden="true" />
                                        <span>{formatDateLabel(event.date)}</span>
                                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#0b2a63]">
                                          Dia inteiro
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <FaClock className="text-slate-400" aria-hidden="true" />
                                        <span>{formatDateTimeLabel(event.date)}</span>
                                      </>
                                    )}
                                  </div>
                                  {event.description && (
                                    <p className="mt-1 text-slate-500">{event.description}</p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 text-xs text-slate-500">
                                  {event.source === "local" ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEditEvent(event)}
                                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-600 transition hover:bg-white"
                                        disabled={savingEvent}
                                      >
                                        <FaEdit className="text-xs" />
                                        Editar
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteEvent(event)}
                                        className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 font-medium text-rose-600 transition hover:bg-rose-50"
                                        disabled={savingEvent}
                                      >
                                        <FaTrash className="text-xs" />
                                        Excluir
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700">
                                      Gerenciado externamente
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-2xl border border-dashed border-blue-200 bg-white p-6 text-center text-[#174a91]">
                        Nenhum evento futuro. Use o formulário ao lado para cadastrar suas atividades.
                      </div>
                    )}

                    {pastLocalEvents.length > 0 && (
                      <div className="mt-6 border-t border-blue-100 pt-4">
                        <p className="text-xs uppercase tracking-[0.35em] text-slate-400">
                          Eventos recentes
                        </p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {pastLocalEvents.map((event) => {
                            const isAllDay =
                              event?.allDay === undefined || event?.allDay === null
                                ? true
                                : Boolean(event.allDay);
                            return (
                              <div
                                key={event.id}
                                className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600"
                              >
                                <p className="font-semibold text-slate-800">{event.title}</p>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                                  {isAllDay ? (
                                    <>
                                      <FaCalendarAlt className="text-slate-400" aria-hidden="true" />
                                      <span>{formatDateLabel(event.date)}</span>
                                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#0b2a63]">
                                        Dia inteiro
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <FaClock className="text-slate-400" aria-hidden="true" />
                                      <span>{formatDateTimeLabel(event.date)}</span>
                                    </>
                                  )}
                                </div>
                                <p className="text-xs uppercase tracking-wide text-slate-400">
                                  {describeDaysUntil(event.daysUntil)}
                                </p>
                                {event.description && (
                                  <p className="mt-1 text-xs text-slate-500">{event.description}</p>
                                )}
                                {event.source === "integration" && (
                                  <p className="mt-1 text-[11px] uppercase tracking-wide text-emerald-600">
                                    {event.integrationName
                                      ? `Sincronizado de ${event.integrationName}`
                                      : "Sincronizado do calendário conectado"}
                                  </p>
                                )}
                                {event.source === "local" ? (
                                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                                    <button
                                      type="button"
                                      onClick={() => handleEditEvent(event)}
                                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-slate-600 transition hover:bg-slate-50"
                                    >
                                      <FaEdit className="text-xs" />
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteEvent(event)}
                                      className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-rose-600 transition hover:bg-rose-50"
                                    >
                                      <FaTrash className="text-xs" />
                                      Excluir
                                    </button>
                                  </div>
                                ) : (
                                  <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">
                                    Gerenciado externamente
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                    {isMasterUser && (
                      <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500/80">
                              integrada
                            </p>
                            <h3 className="text-lg font-semibold text-slate-900">
                              Eventos sincronizados
                            </h3>
                            <p className="text-sm text-slate-600">
                              Acompanhe compromissos trazidos automaticamente dos calendários conectados.
                            </p>
                          </div>
                          {!!connectedIntegrations.length && (
                            <span className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium uppercase tracking-wide text-emerald-600">
                              {connectedIntegrations.length} calendário
                              {connectedIntegrations.length === 1 ? "" : "s"} conectado
                              {connectedIntegrations.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>

                        <div className="mt-4 space-y-4">
                          {calendarLoading ? (
                            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700">
                              Carregando eventos sincronizados...
                            </div>
                          ) : upcomingIntegrationEvents.length ? (
                            upcomingIntegrationEvents.map((event) => {
                              const isAllDay =
                                event?.allDay === undefined || event?.allDay === null
                                  ? true
                                  : Boolean(event.allDay);
                              return (
                                <div
                                  key={event.id}
                                  className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700"
                                >
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">
                                        {describeDaysUntil(event.daysUntil)}
                                      </p>
                                      <h4 className="text-base font-semibold text-emerald-900">
                                        {event.title}
                                      </h4>
                                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-emerald-700">
                                        {isAllDay ? (
                                          <>
                                            <FaCalendarAlt className="text-emerald-500" aria-hidden="true" />
                                            <span>{formatDateLabel(event.date)}</span>
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                              Dia inteiro
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <FaClock className="text-emerald-500" aria-hidden="true" />
                                            <span>{formatDateTimeLabel(event.date)}</span>
                                          </>
                                        )}
                                      </div>
                                      {event.description && (
                                        <p className="mt-1 text-emerald-600">{event.description}</p>
                                      )}
                                    </div>
                                    <div className="flex flex-col gap-2 text-xs text-emerald-600">
                                      <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-3 py-1 font-medium">
                                        <FaPlug className="text-xs" />
                                        {event.integrationName
                                          ? `Sincronizado de ${event.integrationName}`
                                          : "Calendário conectado"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700">
                              {connectedIntegrations.length
                                ? "Nenhum evento sincronizado para os próximos dias. Tente atualizar suas integrações ou verificar o período configurado."
                                : "Conecte um calendário na seção abaixo para visualizar aqui os eventos sincronizados."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="grid gap-6 md:grid-cols-2">
                  {showBirthdaysPanel && (
                    <div className="rounded-2xl border border-rose-100 bg-white p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-rose-400">
                            aniversários
                          </p>
                          <h3 className="text-lg font-semibold text-slate-900">
                            Próximas celebrações
                          </h3>
                        </div>
                        <FaBirthdayCake className="text-2xl text-rose-400" />
                      </div>
                      <div className="mt-4 space-y-3">
                        {nextBirthdays.length ? (
                          nextBirthdays.map((birthday) => (
                            <div
                              key={birthday.userId}
                              className={`rounded-2xl border p-4 ${
                                birthday.isToday
                                  ? "border-rose-300 bg-rose-50"
                                  : "border-rose-100 bg-rose-50"
                              }`}
                            >
                              <p className="text-xs font-semibold uppercase tracking-wide text-rose-500">
                                {describeDaysUntil(birthday.daysUntil)}
                              </p>
                              <h4 className="text-base font-semibold text-slate-900">
                                {birthday.name}
                              </h4>
                              <p className="text-sm text-slate-600">
                                {formatDateLabel(birthday.date)}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="rounded-2xl border border-dashed border-rose-200 bg-rose-50 p-4 text-sm text-rose-500">
                            Nenhum aniversário registrado para os próximos meses.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {showCommemorativePanel && (
                    <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.35em] text-amber-500">
                            datas comemorativas
                          </p>
                          <h3 className="text-lg font-semibold text-slate-900">
                            Feriados em destaque
                          </h3>
                        </div>
                        <FaCalendarAlt className="text-2xl text-amber-500" />
                      </div>
                      <div className="mt-4 space-y-3">
                        {highlightedCommemorativeDates.length ? (
                          highlightedCommemorativeDates.map((item) => (
                            <div
                              key={item.id}
                              className={`rounded-2xl border p-4 ${
                                item.isToday
                                  ? "border-amber-300 bg-amber-50"
                                  : "border-amber-100 bg-amber-50"
                              }`}
                            >
                              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                                {describeDaysUntil(item.daysUntil)}
                              </p>
                              <h4 className="text-base font-semibold text-slate-900">
                                {item.label}
                              </h4>
                              <p className="text-sm text-slate-600">
                                {formatDateLabel(item.date)}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="rounded-2xl border border-dashed border-amber-200 bg-amber-50 p-4 text-sm text-amber-600">
                            As próximas datas comemorativas aparecerão automaticamente aqui.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className={`${agendaTab === "schedules" ? "" : "hidden "}rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-indigo-500/80">
              agendamentos
            </p>
            <h2 className="text-xl font-semibold text-slate-900">
              Solicitações agendadas
            </h2>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {scheduleTimeFilterOptions.map((option) => {
            const active = scheduleTimeFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setScheduleTimeFilter(option.value)}
                className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                  active
                    ? "bg-indigo-600 text-white shadow"
                    : "border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3 text-sm text-slate-700">
          <label className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">De</span>
            <input
              type="date"
              value={scheduleDateFilter.start}
              onChange={(event) =>
                setScheduleDateFilter((prev) => ({ ...prev, start: event.target.value }))
              }
              className="mt-1 rounded border border-indigo-200 px-3 py-1 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Até</span>
            <input
              type="date"
              value={scheduleDateFilter.end}
              onChange={(event) =>
                setScheduleDateFilter((prev) => ({ ...prev, end: event.target.value }))
              }
              className="mt-1 rounded border border-indigo-200 px-3 py-1 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          {hasScheduleDateFilter && (
            <button
              type="button"
              onClick={() => setScheduleDateFilter({ start: "", end: "" })}
              className="rounded border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50"
            >
              Limpar datas
            </button>
          )}
          {scheduleTimeFilter === "completed" && (
            <button
              type="button"
              onClick={handleFetchCompletedSchedules}
              disabled={
                completedLoading || !scheduleDateFilter.start || !scheduleDateFilter.end
              }
              className="rounded border border-indigo-500 bg-indigo-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:border-indigo-200 disabled:bg-indigo-200"
            >
              {completedLoading ? "Consultando..." : "Consultar execuções"}
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {scheduleFilterOptions.map((option) => {
            const active = scheduleFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setScheduleFilter(option.value)}
                className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                  active
                    ? "bg-indigo-600 text-white shadow"
                    : "border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {option.label}
                <span
                  className={`ml-2 inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-[11px] font-semibold ${
                    active ? "bg-white/20 text-white" : "bg-indigo-100 text-indigo-700"
                  }`}
                >
                  {option.count}
                </span>
              </button>
            );
          })}
        </div>

        {scheduleTimeFilter === "completed" && completedError && (
          <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
            {completedError}
          </p>
        )}

        <div className="mt-4">
          {isLoadingSchedules ? (
            <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-600">
              {scheduleLoadingMessage}
            </div>
          ) : filteredSchedules.length ? (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredSchedules.map((schedule) => {
                const nextRunText = formatDateTimeLabel(schedule.nextRunAt);
                const nextRunDescription =
                  nextRunText === "-" ? "Sem próxima execução definida" : nextRunText;
                const recurrence =
                  schedule.type === "RECURRING"
                    ? describeRecurring(schedule)
                    : schedule.type === "MONTHLY"
                      ? describeMonthly(schedule)
                      : null;
                const isCompleted = !schedule.active;
                const initialValues =
                  schedule.config?.initialValues || schedule.config?.variables || {};
                const initialValueCount =
                  initialValues && typeof initialValues === "object"
                    ? Object.keys(initialValues).length
                    : 0;
                const lastRunText = schedule.lastResult?.ranAt
                  ? formatDateTimeLabel(schedule.lastResult.ranAt)
                  : null;
                const isEditingSchedule =
                  scheduleEditorTarget?.id === schedule.id && scheduleEditorOpen;
                return (
                  <div
                    key={schedule.id}
                    className="flex flex-col gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.35em] text-indigo-500">
                          Processo #{schedule.processId}
                        </p>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {schedule.processName}
                        </h3>
                        {schedule.identifier?.value && (
                          <div className="mt-2 rounded-xl border border-indigo-200 bg-white/80 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-indigo-500">
                              {schedule.identifier.label || "Identificador"}
                            </p>
                            <p className="mt-0.5 break-words text-sm font-semibold text-indigo-950">
                              {schedule.identifier.value}
                            </p>
                          </div>
                        )}
                        {schedule.processCategory && (
                          <p className="mt-1 text-xs text-slate-500">
                            Categoria: {schedule.processCategory}
                          </p>
                        )}
                      </div>
                      <span className="inline-flex h-10 w-10 items-center justify-center text-indigo-500">
                        <FaCalendarCheck />
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-slate-600">
                      {isCompleted ? (
                        <p className="flex items-center gap-2 text-slate-700">
                          <FaClock className="text-indigo-500" />
                          {lastRunText
                            ? `Execução finalizada em ${lastRunText}`
                            : "Execução finalizada"}
                        </p>
                      ) : (
                        <p className="flex items-center gap-2 text-slate-700">
                          <FaPlay className="text-indigo-500" />
                          {schedule.type === "ONCE"
                            ? `Execução agendada para ${nextRunDescription}`
                            : `Próxima execução em ${nextRunDescription}`}
                        </p>
                      )}
                      {recurrence && !isCompleted && (
                        <p className="pl-6 text-xs text-slate-500">{recurrence}</p>
                      )}
                      <p className="text-xs text-slate-500">
                        {schedule.type === "ONCE"
                          ? "Agendamento único"
                          : schedule.type === "MONTHLY"
                            ? "Agendamento mensal"
                            : "Agendamento semanal"}
                      </p>
                      {initialValueCount > 0 && schedule.createdByMe && (
                        <p className="text-xs font-medium text-[#0b2a63]">
                          {initialValueCount} dado{initialValueCount === 1 ? "" : "s"} pré-definido{initialValueCount === 1 ? "" : "s"}
                        </p>
                      )}
                      <p className="flex items-center gap-2 text-xs text-slate-500">
                        <FaUser className="text-slate-400" />
                        {schedule.createdBy?.name || "—"}
                      </p>
                      {!isCompleted && schedule.lastResult?.ranAt && (
                        <p className="flex items-center gap-2 text-xs text-slate-500">
                          <FaClock className="text-slate-400" />
                          Última execução em {formatDateTimeLabel(schedule.lastResult.ranAt)}
                        </p>
                      )}
                      {isCompleted && schedule.lastResult && (
                        <div
                          className={`rounded-xl border px-3 py-2 text-xs ${
                            schedule.lastResult.status === "SUCCESS"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-rose-200 bg-rose-50 text-rose-700"
                          }`}
                        >
                          {schedule.lastResult.status === "SUCCESS" ? "Concluído" : "Falha"}
                          {schedule.lastResult.message ? `: ${schedule.lastResult.message}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {schedule.createdByMe && (
                        <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                          Você agendou
                        </span>
                      )}
                      {schedule.willAssignToMe && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                          Vai chegar para você
                        </span>
                      )}
                      {isCompleted && (
                        <span className="inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                          Inativo
                        </span>
                      )}
                      {schedule.runCount > 0 && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {schedule.runCount} execução{schedule.runCount === 1 ? "" : "es"}
                        </span>
                      )}
                    </div>
                    {schedule.createdByMe && !isCompleted && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => openScheduleEditor(schedule)}
                          className="inline-flex items-center gap-1 rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-700 transition hover:bg-white"
                          disabled={savingScheduleUpdate && isEditingSchedule}
                        >
                          <FaEdit className="text-xs" />
                          {isEditingSchedule && savingScheduleUpdate ? "Reagendando..." : "Reagendar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteSchedule(schedule)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
                          disabled={removingScheduleId === schedule.id}
                        >
                          <FaTrash className="text-xs" />
                          {removingScheduleId === schedule.id ? "Cancelando..." : "Cancelar"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-600">
              {scheduleEmptyMessage}
            </div>
          )}
        </div>
      </section>

      <Modal
        open={calendarModalOpen}
        onClose={closeCalendarModal}
        size="max-w-5xl"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Calendário completo
              </h2>
              <p className="text-sm text-slate-600">
                Visualize eventos, aniversários e datas comemorativas em uma única visão mensal.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                aria-label="Mês anterior"
              >
                <FaChevronLeft />
              </button>
              <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 capitalize">
                {calendarMonthLabel}
              </span>
              <button
                type="button"
                onClick={goToNextMonth}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                aria-label="Próximo mês"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <button
              type="button"
              onClick={goToToday}
              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
            >
              Hoje
            </button>
          </div>

          {nextBirthdays.length > 0 && (
            <div className="rounded-3xl border border-rose-100 bg-gradient-to-r from-rose-50 via-pink-50 to-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-500 text-white shadow-sm">
                    <FaBirthdayCake />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-rose-400">Minha agenda</p>
                    <h3 className="text-base font-semibold text-slate-900">Aniversários no calendário</h3>
                  </div>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
                  {nextBirthdays.slice(0, 4).map((birthday) => (
                    <span
                      key={`calendar-birthday-${birthday.userId}`}
                      className="whitespace-nowrap rounded-full border border-rose-100 bg-white px-3 py-1 text-xs font-medium text-rose-700 shadow-sm"
                    >
                      {birthday.name} · {formatDateLabel(birthday.date)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="grid grid-cols-7 gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {WEEKDAY_SHORT.map((weekday) => (
                <div key={weekday} className="text-center">
                  {weekday}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2 text-xs">
              {calendarWeeks.flatMap((week, weekIndex) =>
                week.map((day, dayIndex) => {
                  const cellKey = `${day.key}-${weekIndex}-${dayIndex}`;
                  const visibleItems = day.items.slice(0, 3);
                  const hiddenCount = day.items.length - visibleItems.length;
                  const cellClasses = [
                    "flex min-h-[110px] cursor-pointer flex-col gap-1 rounded-xl border p-2 transition sm:min-h-[130px]",
                    day.inMonth ? "bg-white" : "bg-slate-50 text-slate-400",
                    day.isToday ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200",
                  ].join(" ");
                  return (
                    <div key={cellKey} className={cellClasses} onClick={() => handleSelectCalendarDay(day.date)}>
                      <div className="flex items-center justify-between text-[11px] font-semibold">
                        <button type="button" aria-label={`Novo evento em ${day.date.toLocaleDateString("pt-BR")}`} className={day.inMonth ? "text-slate-600" : "text-slate-400"}>
                          {day.date.getDate()}
                        </button>
                        {day.items.length > 0 && (
                          <span className="text-[10px] font-medium text-slate-400">
                            {day.items.length}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 space-y-1 text-[11px] leading-tight">
                        {visibleItems.map((item, itemIndex) => {
                          const itemKey = `${cellKey}-item-${itemIndex}`;
                          let badgeClass = "bg-blue-100 text-[#0b2a63]";
                          let icon = <FaCalendarCheck className="text-[10px]" />;
                          let label = item.label;
                          if (item.type === "event") {
                            if (item.source === "integration") {
                              label = `${item.label} (integração)`;
                            }
                            if (!item.allDay && item.timeLabel) {
                              icon = <FaClock className="text-[10px]" />;
                              label = `${item.timeLabel} · ${label}`;
                            }
                          } else if (item.type === "birthday") {
                            badgeClass = "bg-rose-100 text-rose-700";
                            icon = <FaBirthdayCake className="text-[10px]" />;
                            label = `Aniversário de ${item.label}`;
                            if (item.turningAge) {
                              label += ` (${item.turningAge} anos)`;
                            }
                          } else if (item.type === "commemorative") {
                            badgeClass = "bg-amber-100 text-amber-700";
                            icon = <FaBell className="text-[10px]" />;
                            label = item.label;
                          }
                          return (
                            <button
                              type="button"
                              onClick={e => { if (item.type === "event") { e.stopPropagation(); handleEditEvent(item.event); } }}
                              key={itemKey}
                              className={`flex w-full items-center gap-1 rounded-lg px-2 py-1 text-left ${badgeClass}`}
                            >
                              {icon}
                              <span className="truncate">{label}</span>
                            </button>
                          );
                        })}
                        {hiddenCount > 0 && (
                          <p className="text-[10px] text-slate-400">
                            +{hiddenCount} lembrete{hiddenCount === 1 ? "" : "s"}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-blue-400" />
              Eventos pessoais
            </span>
            <span className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
              Aniversários
            </span>
            <span className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              Datas comemorativas
            </span>
          </div>
        </div>
      </Modal>

      <Modal
        open={scheduleEditorOpen}
        onClose={savingScheduleUpdate ? () => {} : closeScheduleEditor}
        size="max-w-5xl"
      >
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Reagendar execução automática
            </h3>
            <p className="text-sm text-slate-600">
              Ajuste o horário, a recorrência ou os dados pré-definidos do processo
              {scheduleEditorTarget?.processName
                ? ` "${scheduleEditorTarget.processName}"`
                : ""}
              .
            </p>
            {scheduleEditorTarget?.identifier?.value && (
              <div className="mt-2 inline-flex max-w-full flex-col rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-500">
                  {scheduleEditorTarget.identifier.label || "Identificador"}
                </span>
                <span className="break-words text-sm font-semibold text-indigo-950">
                  {scheduleEditorTarget.identifier.value}
                </span>
              </div>
            )}
          </div>

          <ScheduleConfigurator
            processId={scheduleEditorTarget?.processId}
            value={scheduleEditorState}
            onChange={setScheduleEditorState}
            allowDisable={false}
          />

          {scheduleEditorError && (
            <p className="text-sm text-rose-600">{scheduleEditorError}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeScheduleEditor}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              disabled={savingScheduleUpdate}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleScheduleUpdate}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-700 disabled:opacity-60"
              disabled={savingScheduleUpdate}
            >
              {savingScheduleUpdate ? "Salvando..." : "Atualizar"}
            </button>
          </div>
        </div>
      </Modal>

      {isMasterUser && (
      <section className="rounded-3xl border border-blue-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.4em] text-blue-500/80">
                integrações
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Conecte seu calendário
              </h2>
              <p className="text-sm text-slate-600">
                Sincronize automaticamente com Google Calendar ou qualquer provedor compatível com links iCal.
              </p>
            </div>
            <button
              type="button"
              onClick={loadCalendarSummary}
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-4 py-2 text-sm font-medium text-[#0b2a63] transition hover:bg-blue-50"
            >
              <FaSyncAlt className={calendarLoading ? "animate-spin" : ""} />
              Atualizar dados
            </button>
          </div>

          <div className="mt-6 space-y-4">
            {connectedIntegrations.length ? (
              connectedIntegrations.map((integration) => {
                const isSyncing = syncingIntegrationIds.includes(integration.id);
                const isUpdating = updatingIntegrationIds.includes(integration.id);
                const isRemoving = removingIntegrationIds.includes(integration.id);
                return (
                  <div
                    key={integration.id}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1">
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <FaPlug className="text-base text-emerald-500" />
                          {integration.displayName || integration.providerLabel || "Integração"}
                        </p>
                        {integration.description && (
                          <p className="text-xs text-slate-500">{integration.description}</p>
                        )}
                        {(
                          integration.lastSyncedAt || integration.lastSyncAt
                        ) && (
                          <p className="text-xs text-slate-500">
                            Última sincronização: {formatDateTimeLabel(
                              integration.lastSyncedAt || integration.lastSyncAt
                            )}
                          </p>
                        )}
                        {integration.lastSyncStatus && (
                          <p className="text-xs text-rose-500">
                            {integration.lastSyncStatus}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs font-medium">
                        <button
                          type="button"
                          onClick={() => handleSyncIntegration(integration)}
                          className="inline-flex items-center gap-1 rounded-full border border-blue-200 px-3 py-1 text-[#174a91] transition hover:bg-blue-50"
                          disabled={isSyncing}
                        >
                          <FaSyncAlt className={isSyncing ? "animate-spin" : ""} />
                          Sincronizar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateIntegration(integration)}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-emerald-600 transition hover:bg-emerald-50"
                          disabled={isUpdating}
                        >
                          <FaEdit /> Atualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteIntegration(integration)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-rose-600 transition hover:bg-rose-50"
                          disabled={isRemoving}
                        >
                          <FaTrash /> Remover
                        </button>
                      </div>
                    </div>
                    {integration.connectionDetails && (
                      <p className="mt-3 flex items-center gap-2 rounded-2xl border border-blue-100 bg-white px-3 py-2 text-xs text-[#0b2a63]">
                        <FaLink /> {integration.connectionDetails}
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-6 text-center text-sm text-[#174a91]">
                Nenhuma integração conectada. Use o formulário abaixo para vincular seu calendário.
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmitIntegration}
            className="mt-6 space-y-4 rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-inner"
          >
            <div className="flex items-center gap-2 text-[#0b2a63]">
              <FaPlug className="text-lg" />
              <h3 className="text-base font-semibold">Conectar novo calendário</h3>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                  Provedor
                </label>
                <select
                  value={integrationForm.provider}
                  onChange={(event) =>
                    handleIntegrationInputChange("provider", event.target.value)
                  }
                  className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  disabled={savingIntegration}
                >
                  <option value="google">Google Calendar</option>
                  <option value="microsoft">Outlook / Microsoft 365</option>
                  <option value="apple">Apple Calendar</option>
                  <option value="other">Outro provedor</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                  Nome para exibição (opcional)
                </label>
                <input
                  type="text"
                  value={integrationForm.displayName}
                  onChange={(event) =>
                    handleIntegrationInputChange("displayName", event.target.value)
                  }
                  className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Ex.: Calendário corporativo"
                  disabled={savingIntegration}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-[#174a91]">
                Link de assinatura (iCal)
              </label>
              <input
                type="url"
                value={integrationForm.sourceUrl}
                onChange={(event) =>
                  handleIntegrationInputChange("sourceUrl", event.target.value)
                }
                className="w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="https://calendar.google.com/calendar/ical/..."
                disabled={savingIntegration}
                required
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-70"
                disabled={savingIntegration}
              >
                Conectar calendário
              </button>
              <button
                type="button"
                onClick={() =>
                  setIntegrationForm({ provider: "google", sourceUrl: "", displayName: "" })
                }
                className="rounded-full border border-blue-200 px-4 py-2 text-sm font-medium text-[#174a91] transition hover:bg-blue-50"
                disabled={savingIntegration}
              >
                Limpar formulário
              </button>
            </div>

            <p className="text-xs text-[#0b2a63]">
              Aceitamos links iCal/ICS públicos ou privados. Para calendários privados, utilize um link de assinatura dedicado.
            </p>
          </form>
        </section>
      )}



    </div>
  );
}
